import React, { useState } from 'react';
import { RadialChart, makeVisFlexible, LabelSeries } from 'react-vis';
import {
    Nav,
    NavItem,
    NavLink,
    Card,
    CardTitle,
    CardBody,
    CardText,
    CardHeader,
    CardDeck,
    Row,
    Col,
    Media,
    Table,
    CardFooter,
    CustomInput,
    Button,
    UncontrolledTooltip,
} from 'reactstrap';
import isAfter from 'date-fns/isAfter';
import startOfDay from 'date-fns/startOfDay';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { fetcher } from 'api/client';
import { parseDate } from 'utils';
import { useTranslation } from 'react-i18next';
import { useProfileAbility, useProfileState } from 'context/profile';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { fetchCounters, fetchEvents, fetchAmount, fetchMessages, getSignedByTime } from 'api/actions/dashboard';
import { getAnalytics } from 'api/actions/reports';
import { getContractTypes } from 'api/actions/dashboard';
import { Link, useHistory } from 'react-router-dom';
import { colorRanges } from '../Report/ReportConfig';
import { queries } from './counters';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledDropdown } from 'reactstrap';
import { updateProfile } from 'api/actions/profile';
import styled from 'styled-components';
import { Ellipsis } from 'components/Shared/Ellipsis';
import { toast } from 'components/Shared/Toast';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import createPersistedState from 'use-persisted-state';
import TratoLoader from 'components/Shared/TratoLoader';

const useHideBalanceState = createPersistedState('hide-balance');

const Minibox = styled(Card)`
    cursor: move;
    max-height: 96px;
    min-height: 96px;
    max-width: 108px;
    min-width: 108px;
`;

function DropMenuAmounts() {
    const [hideBalance, setHideBalance] = useHideBalanceState()
    const onClick = () => setHideBalance(!hideBalance)
    const ability = useProfileAbility();

    if (ability.cannot('DASHBOARD_VIEW_BALANCE')) return null;

    return (
        <UncontrolledDropdown className="drop-down-margin" style={{ height: '0px', position: 'absolute', right: '0px', top: '-5px' }}>
            <DropdownToggle tag="span" onClick={onClick}>
                <Ellipsis />
            </DropdownToggle>
        </UncontrolledDropdown>
    );
}

function DropMenu() {
    return (
        <UncontrolledDropdown className="drop-down-margin" style={{ height: '0px', position: 'absolute', right: '0px', top: '-5px' }}>
            <DropdownToggle tag="span">
                <Ellipsis />
            </DropdownToggle>
            {/*
            <DropdownMenu>
                <DropdownItem>Some Action</DropdownItem>
                <DropdownItem text>Dropdown Item Text</DropdownItem>
            </DropdownMenu>
            */}
        </UncontrolledDropdown>
    );
}

export const Minicard = React.forwardRef(({ counter, index, signInOrder, jurisdiction, ...props }, ref) => {
    const { t } = useTranslation();
    const history = useHistory();

    const { url, name } = queries.find((q) => q.query === counter?.name) || {};

    const handleCounter = () => {
        history.push(url || `/search-contracts/${name}`);
    };

    return (
        <Minibox
            body
            id={name}
            className={clsx('text-center rounded-lg pointer mb-2', name || 'bg-light-gray pulse-animation')}
            onClick={handleCounter}
            innerRef={ref}
            {...props}
        >
            <CardTitle tag="h4" className="mb-1 font-weight-bold">
                {counter?.value}
            </CardTitle>
            <CardText tag="small" className="text-muted">
                {name && t(`dashboard.counter.${name}`)}
            </CardText>
        </Minibox>
    );
});


function Balance() {
    const [hideBalance] = useHideBalanceState();
    const ability = useProfileAbility();
    const { data = {} } = useQuery('fetch-amount', () => fetcher(fetchAmount()), { enabled: !hideBalance && ability.can('DASHBOARD_VIEW_BALANCE') });
    const { t } = useTranslation();

    if (hideBalance || ability.cannot('DASHBOARD_VIEW_BALANCE')) return null;

    return (
        <Card className="rounded-lg border bg-white card-shadow mt-4 py-2">
            <CardBody className="d-flex flex-column justify-content-left py-0">
                <hr className="d-lg-none"></hr>
                <h4 className="my-2" style={{ font: 'normal normal bold 18px Muli' }}>
                    {t('dashboard.amounts.title')}
                </h4>
                <small className="small text-muted">{t('dashboard.amounts.subtitle')}</small>
                <CardDeck className="pt-4 mr-xl-auto float-left">
                    {Object.entries(data).map(([key, value]) => (
                        <Card key={key} className="bg-transparent border-0 text-left pb-0">
                            <CardTitle tag="h4" className="m-0 font-weight-bold">
                                ${value}
                            </CardTitle>
                            <CardText tag="small" className="text-muted">
                                {t(`dashboard.amounts.${key}`)}
                            </CardText>
                        </Card>
                    ))}
                </CardDeck>
            </CardBody>
        </Card>
    );
}


const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',

    // change background colour if dragging
    // background: isDragging ? '#45616e' : '#354f5c',

    // styles we need to apply on draggables
    ...draggableStyle
});


function Overview() {
    const { profile } = useProfileState();
    const queryClient = useQueryClient();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { t } = useTranslation();
    const [filters, setFilters] = useState([]);
    const miniCardLimit = 6;
    const ability = useProfileAbility();
    let datas = filters

    const { data = [], isSuccess } = useQuery(['fetch-counters'], () => fetcher(fetchCounters()), {
        onSuccess: (data) => {
            setFilters(data.map(d => d.name));
        }
    });

    const { mutate: mutateUpdateProfile, isLoading } = useMutation((payload) => fetcher(updateProfile(payload)), {
        onSuccess: () => {
            if (dropdownOpen) toggle();
            toast.success(t('common.success'));
            queryClient.invalidateQueries(['fetch-counters']);
        },
    });

    const toggle = () => {
        if (!isSuccess) return null;
        setDropdownOpen((prevState) => !prevState);
    }

    const handleSave = () => {
        mutateUpdateProfile({
            customizations: {
                dashboard: {
                    counters: filters
                },
            },
        });
    };

    const handleChange = (e) => {
        if (e.target.checked) {
            setFilters([...filters, e.target.name]);
        } else {
            setFilters([...filters.filter((f) => f !== e.target.name)])
        }
    }

    const moveInArray = (arr, from, to) => {
        // Make sure a valid array is provided
        if (Object.prototype.toString.call(arr) !== '[object Array]') throw new Error('Please provide a valid array');
        // Delete the item from it's current position
        let item = arr.splice(from, 1);
        // Make sure there's an item to move
        if (!item.length) throw new Error('There is no item in the array at index ' + from);
        // Move the item to its new position
        arr.splice(to, 0, item[0]);
    };

    const handleDragEnd = (result) => {
        //dropped outside the list
        if (!result.destination || filters[result.destination.index] === filters[result.source.index]) {
            return;
        }
        moveInArray(filters, result.source.index, result.destination.index)
        handleSave();
    }

    return (
        <Col xl="9" className="mb-4 px-75">
            <Card className="h-100 rounded-lg border bg-white card-shadow">
                <Dropdown isOpen={dropdownOpen} toggle={toggle} className="text-right drop-down-margin">
                    <DropdownToggle tag="span">
                        <Ellipsis />
                    </DropdownToggle>

                    <DropdownMenu>
                        <DropdownItem header className="text-center">{t('dashboard.label.counters')}</DropdownItem>
                        {queries.map((e) =>
                            <DropdownItem toggle={false} key={e.name}>
                                <CustomInput
                                    disabled={e.rol && ability.cannot(e.rol)}
                                    onChange={handleChange}
                                    type="checkbox"
                                    defaultChecked={filters.includes(e.query)}
                                    name={e.query}
                                    id={e.query}
                                    label={t(`dashboard.counter.${e.name}`)}
                                    className="label-padding"
                                />
                            </DropdownItem>
                        )}
                        <DropdownItem header className="text-center" hidden={filters.length === miniCardLimit}>
                            {t('dashboard.messages.cardlimit', { count: miniCardLimit })}
                        </DropdownItem>

                        <Button
                            size="sm"
                            color="default"
                            block
                            onClick={handleSave}
                            className="text-center"
                            disabled={filters.length !== miniCardLimit || isLoading}
                        >
                            {t('dashboard.activities.save')}
                        </Button>
                    </DropdownMenu>
                </Dropdown>
                <CardBody className="h-100 d-flex flex-column justify-content-center pb-2 pt-0">
                    <Row>
                        <Col xl="12" className="pb-3 pt-0 px-5">
                            <h3 style={{ font: 'normal normal bold 24px/24px Muli' }}>
                                {t('dashboard.greeting')} {profile?.fullName}!
                            </h3>
                            <small className="small text-muted muli-regular">{t('dashboard.description')}</small>
                        </Col>
                    </Row>
                    {!isLoading &&
                        <DragDropContext onDragEnd={handleDragEnd} >
                            <Droppable droppableId="droppable" isDropDisabled={false} direction="horizontal" style={{ cursor: 'move' }}>{
                                (provided, snapshot) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="m-0 mx-auto card-deck"
                                    >
                                        {
                                            data?.map((counter, index) => (
                                                <Draggable
                                                    key={counter.name}
                                                    draggableId={counter.name}
                                                    index={index}
                                                    isDragDisabled={false}
                                                >
                                                    {(provided, snapshot) => (
                                                        <Minicard
                                                            counter={counter}
                                                            key={`minicard-${index}`}
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            style={getItemStyle(
                                                                snapshot.isDragging,
                                                                provided.draggableProps.style
                                                            )}
                                                        />
                                                    )}
                                                </Draggable>
                                            ))
                                        }
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    }
                    {isLoading && <div className="m-0 mx-auto card-deck">{[...Array(6)].map((e, index) => <Minicard key={`minicard-empty-${index}`} isLoading={isLoading} />)}</div>}
                </CardBody>
            </Card>
        </Col>
    );
}

function Profile() {
    const { profile } = useProfileState();
    const { t } = useTranslation();

    return (
        <Col xl="3" className="mb-4 px-75">
            <Card className="h-100 rounded-lg border bg-white card-shadow min-h-25">
                <CardBody className="h-100 d-flex flex-column justify-content-center p-3">
                    <img src={profile.photo || '/images/owl-logo.svg'} className="mx-auto profile-img-border mb-2" width="79" alt="profile-img" />
                    <h5 className="mx-auto mt-2 text-center">{profile?.fullName}</h5>
                    <small className="mx-auto text-muted mb-3">{t('dashboard.label.company')}:</small>
                    <img className="mx-auto company-img-size" src={profile.tenant?.branding?.logo || '/images/logo-01.svg'} alt="brand-logo" />
                </CardBody>
            </Card>
        </Col>
    );
}

function Activity() {

    const [activeTab, setActiveTab] = useState({ tab: 'activity', firstTime: true });

    const toggle = (tabName) => activeTab.tab !== tabName && setActiveTab({ tab: tabName });

    const { t } = useTranslation();

    const [dates] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });

    const { data: activities = [], isLoading: isLoadingActivities } = useQuery(['fetch-events', dates], () =>
        fetcher(fetchEvents(dates)), {
        enabled: activeTab.tab === 'activity' || !activeTab.firstTime,
        keepPreviousData: true,
        refetchOnWindowFocus: false
    });

    const { data: messages = [], isLoading: isLoadingMessages } = useQuery('fetch-messages', () =>
        fetcher(fetchMessages()), {
        enabled: activeTab.tab === 'comments' || !activeTab.firstTime,
        keepPreviousData: true,
        refetchOnWindowFocus: false
    });

    return (
        <Col xl="6" className="mb-4 px-75">
            <Card className="rounded-lg bg-transparent border-0 h-100">
                <Card className="border bg-white card-shadow rounded-lg h-100" style={{ minHeight: '250px' }}>
                    <DropMenuAmounts />
                    <CardHeader className="bg-transparent border-white text-left">
                        <Nav>
                            {['activity', 'comments'].map((tabName) => (
                                <NavItem className="px-2" key={tabName}>
                                    <NavLink
                                        className={
                                            clsx('custom-nav-style px-0 ml-2', {
                                                'active-tab': activeTab.tab === tabName,
                                            })
                                        }
                                        onClick={() => {
                                            toggle(tabName);
                                        }}
                                    >
                                        {t(`dashboard.label.${tabName}`)}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>
                    </CardHeader>
                    <CardBody className="pt-0 table-responsive">
                        <div className="smart-wrapper">
                            <div className="smart-body-wrap w-100 overflow-sm-device">
                                {{
                                    'comments': <TableComments isLoading={isLoadingMessages} messages={messages} />,
                                    'activity': <TableActivity isLoading={isLoadingActivities} activities={activities} />,
                                }[activeTab.tab]}
                            </div>
                        </div>
                    </CardBody>
                </Card>
                <Balance />
            </Card>
        </Col>
    );
}


const groupBy = (array) => {
    return array.reduce((result, currentValue) => {
        (result[currentValue?.contract?._id] = result[currentValue?.contract?._id] || []).push(
            currentValue
        );
        return result;
    }, {});
};

function TableActivity({ activities = [], isLoading }) {

    const { t } = useTranslation();

    if (isLoading) return <TratoLoader className="absolute-center" />;

    return (
        <Table hover className="border-collapse pt-0 pb-2 px-4 table-layout-fixed w-100">
            <thead className="table-borderless">
                <tr>
                    <th className="font-weight-normal sticky-thead bg-white" style={{ width: '115px' }}>
                        {t('dashboard.label.date')}
                    </th>
                    <th className="font-weight-normal sticky-thead bg-white">{t('dashboard.label.activity')}</th>
                    <th className="font-weight-normal text-right sticky-thead text-nowrap bg-white" style={{ width: '122px' }}>
                        {t('dashboard.label.docnum')}
                    </th>
                </tr>
            </thead>
            <tbody>
                {activities.map((event) => (
                    <tr key={event._id}>
                        <td className={'text-nowrap'}>{parseDate(event.eventDate, 'PP')}</td>
                        <td className="text-truncate" id={'id' + event._id}>
                            {event.who} {t(`events.${event.type}`)} {event.to}{' '}
                            <UncontrolledTooltip placement="top" target={'id' + event._id}>
                                {event.who} {t(`events.${event.type}`)} {event.to}{' '}
                            </UncontrolledTooltip>
                        </td>
                        <td className="text-right">
                            <Link to={`/contracts/editor/${event.contract?._id}`} className="text-dark">
                                {event.contract?.documentId}
                            </Link>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}

function TableComments({ messages = [], isLoading }) {

    const { t } = useTranslation();

    const filteredComments = [];

    for (const [key, value] of Object.entries(groupBy(messages))) {
        filteredComments.push({
            name: value[0]?.contract?.name,
            eventDate: parseDate(value[0]?.eventDate, 'PP'),
            comments: value.length,
            _id: key
        });
    }

    if (isLoading) return <TratoLoader className="absolute-center" />;

    return (
        <Table hover className="border-collapse pt-0 pb-2 px-4 table-layout-fixed w-100">
            <thead className="table-borderless">
                <tr>
                    <th className="font-weight-normal sticky-thead bg-white" style={{ width: '115px' }}>
                        {t('dashboard.label.date')}
                    </th>
                    {/* {t('editor.notifications.date')} Fecha de envio */}
                    <th className="font-weight-normal sticky-thead bg-white">{t('dashboard.label.document')}</th>
                    <th className="font-weight-normal text-right sticky-thead bg-white" style={{ width: '122px' }}>
                        {t('dashboard.label.comments')}
                    </th>
                </tr>
            </thead>
            <tbody>
                {filteredComments?.map((e) => (
                    <tr key={e._id}>
                        <td className="text-nowrap">{e?.eventDate}</td>
                        <td className="text-truncate">
                            <Link to={`/contracts/editor/${e?._id}?comments`} className="text-dark">
                                {e?.name}
                            </Link>
                        </td>
                        <td className="text-right">{e?.comments}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}

function ChartStages({ analytics = [], isLoading }) {

    const { t } = useTranslation();

    const ability = useProfileAbility();

    const timeMetrics = analytics.filter((r) => ['time'].includes(r.unit));
    const totalMinutes = String(timeMetrics?.reduce((sum, li) => sum + li.minutes, 0))
    const FlexRadialChart = makeVisFlexible(RadialChart);

    if (isLoading) return <TratoLoader className="absolute-center" />;

    return (
        <>
            <span className="pt-3 muli-regular" hidden={ability.can('DASHBOARD_VIEW_FULL_STATS')}>
                {t('dashboard.label.chart.averagetime')}
            </span>

            <small className="text-muted d-block mt-2 mb-3">
                {t('dashboard.label.chart.lastupdate')} {parseDate(new Date().toISOString(), 'PPp')}
            </small>

            {timeMetrics.map((r, index) => (
                <div key={`analytics-${index}`} className="clearfix text-muted">
                    <span className="float-left text-opacity">{t(`reports.${r.metric}`)}</span>
                    <span className="float-right text-opacity">{r.minutes} min</span>
                </div>
            ))}

            <div className="overflow-hidden h-100" style={{ minHeight: '205px' }}>

                <FlexRadialChart
                    colorRange={colorRanges}
                    data={
                        timeMetrics?.map((r, i) => ({
                            name: r._id,
                            total: r.minutes,
                            angle: r.minutes,
                            color: i + 1,
                            innerRadius: 0.6,
                        })) || []
                    }
                >
                    <LabelSeries
                        className="muli-big med-opacity"
                        style={{
                            dominantBaseline: 'middle',
                            alignmentBaseline: 'middle',
                            textAnchor: 'middle',
                        }}
                        labelAnchorX="central"
                        labelAnchorY="central"
                        data={[{ x: 0, y: 0, yOffset: 0, label: totalMinutes }]}
                    />
                    <LabelSeries
                        className="alt-y-label muli-small med-opacity"
                        style={{
                            dominantBaseline: 'middle',
                            alignmentBaseline: 'middle',
                            textAnchor: 'middle',
                        }}
                        labelAnchorX="central"
                        labelAnchorY="central"
                        data={[{ x: 0, y: 0, yOffset: 20, label: t('dashboard.label.chart.total') }]}
                    />
                </FlexRadialChart>

            </div>
        </>
    )
}

function SignedContracts({ signedContracts = [], isLoading }) {

    const { t } = useTranslation();

    const FlexRadialChart = makeVisFlexible(RadialChart);

    if (isLoading) return <TratoLoader className="absolute-center" />;

    return (
        <>
            <div>
                {signedContracts.map((counter, index) => (
                    <div key={`contracts-${index}`} className="clearfix d-flex">
                        <Link to={`search-contracts/${counter.name}`}>
                            <span className="text-opacity">
                                {t(`dashboard.counter.${counter.name}`)}
                            </span>
                        </Link>
                        <div className="ml-auto align-self-center">
                            <div class="badge-circle text-white" style={{ backgroundColor: colorRanges[index] }}>
                                <span >{counter?.value}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="overflow-hidden h-100" style={{ minHeight: '205px' }}>
                <FlexRadialChart
                    data={signedContracts.map((m, index) => ({ label: m.name, angle: m.value, color: index - 1 }))}
                    colorRange={colorRanges}
                />
            </div>
        </>
    )
}

function ChartContractTypes({ contractTypes = [], isLoading }) {

    const FlexRadialChart = makeVisFlexible(RadialChart);

    if (isLoading) return <TratoLoader className="absolute-center" />;

    return (<>
        <div>
            {contractTypes.map((event, index) => (
                <div key={`contracts-${index}`} className="clearfix">
                    <span className="float-left text-opacity">{event.name}</span>
                    <span className="float-right badge-circle" style={{ backgroundColor: colorRanges[index] }}>
                        {event?.contracts}
                    </span>
                </div>
            ))}
        </div>
        <div className="overflow-hidden h-100" style={{ minHeight: '205px' }}>
            <FlexRadialChart
                data={contractTypes.map((m, index) => ({ label: m.name, angle: m.contracts, color: index + 1 }))}
                colorRange={colorRanges}
            />
        </div>
    </>)
}

const PanelType = {
    stages: 'stages',
    contracttype: 'contracttype',
    signedcontracts: 'signedcontracts',
}

const panels = [
    { name: PanelType.stages, privilege: 'DASHBOARD_VIEW_CONTRACT_STAGES' },
    { name: PanelType.contracttype, privilege: 'DASHBOARD_VIEW_CONTRACT_TYPES' },
    { name: PanelType.signedcontracts, privilege: 'DASHBOARD_VIEW_SIGNED_CONTRACTS' }
]

function Stats() {

    const { t, i18n } = useTranslation();

    const ability = useProfileAbility();

    const [activeTab, setActiveTab] = useState({
        tab: panels.find(panel => ability.can(panel.privilege))?.name
    });

    const toggle = (tabName) => activeTab.tab !== tabName && setActiveTab({ tab: tabName });

    const { profile } = useProfileState();

    const { data: analytics = [], isLoading: isLoadingAnalytics } = useQuery(
        'fetch-analytics',
        () =>
            fetcher(
                getAnalytics({
                    selectedUsers: [],
                    isApplication: 'true',
                    endDate: new Date(),
                    startDate: '1977-01-01T00:00:00.000Z',
                    lang: i18n.language,
                    timeInDays: true,
                    metrics: 'time',
                })
            ),
        {
            enabled: !!profile._id && activeTab.tab === PanelType.stages,
            refetchOnWindowFocus: false,
        }
    );

    const { data: contractTypes = [], isLoading: isLoadingContractTypes } = useQuery('fetch-contract-types', () =>
        fetcher(getContractTypes()),
        {
            enabled: activeTab.tab === PanelType.contracttype,
            refetchOnWindowFocus: false,
        }
    );

    const { data: signedContracts = [], isLoading: isLoadingSigned } = useQuery('fetch-contracts-signed', () =>
        fetcher(getSignedByTime()),
        {
            enabled: activeTab.tab === PanelType.signedcontracts,
            refetchOnWindowFocus: false,
        }
    );

    return (
        <Col xl="3" className="mb-4 px-75" hidden={panels.every(panel => ability.cannot(panel.privilege))}>
            <Card className="rounded-lg border bg-white card-shadow rounded-lg h-100" style={{ minHeight: '450px' }}>
                <CardHeader className="card-header bg-transparent border-white px-0 text-center">
                    <Nav className="justify-content-center">
                        {panels.map((panel) => (
                            <NavItem className="px-2" key={panel.name} hidden={panel.privilege && ability.cannot(panel.privilege)}>
                                <NavLink
                                    className={
                                        clsx('custom-nav-style px-0', {
                                            'active-tab': activeTab.tab === panel.name,
                                        })
                                    }
                                    onClick={() => {
                                        toggle(panel.name);
                                    }}
                                >
                                    {t(`dashboard.label.chart.${panel.name}`)}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>
                </CardHeader>

                <CardBody className="pt-0 text-muted text-center w-100 h-100">
                    <div className="d-flex flex-column h-100 overflow-hidden">
                        <div className="overflow-hidden d-flex flex-column h-100">
                            {{
                                'stages': <ChartStages analytics={analytics} isLoading={isLoadingAnalytics} />,
                                'contracttype': <ChartContractTypes contractTypes={contractTypes} isLoading={isLoadingContractTypes} />,
                                'signedcontracts': <SignedContracts signedContracts={signedContracts} isLoading={isLoadingSigned} />
                            }[activeTab.tab]}
                        </div>
                    </div>
                </CardBody>
            </Card>
        </Col>
    );
}


function Event({ event }) {
    const { eventDate, type, contract, who, to, properties } = event;
    const { t } = useTranslation();

    return (
        <div className="rounded-lg event-shadow p-3 my-4 mx-3">
            <Media>
                <i className="event-status-pending mr-3" style={{ position: 'relative', top: '3px' }}></i>
                <Media body>
                    <h6 className="mb-0 pb-2" style={{ font: 'normal normal normal 14px/18px Muli' }}>
                        {contract?.name}
                    </h6>
                    <small className="text-muted d-block pb-2" style={{ font: 'normal normal normal 11px/14px Muli' }}>
                        {who} {t(`events.${type}`)} {to}
                        {properties?.attachment}
                    </small>
                    <small className="text-muted d-block pb-2" style={{ font: 'normal normal normal 11px/14px Muli' }}>
                        <i className="fas fa-clock"></i> <span>{parseDate(eventDate)}</span>
                    </small>
                </Media>
            </Media>
        </div>
    );
}

function CalendarElement({ events, toggle }) {

    const { t, i18n } = useTranslation();

    const todayEventcounter = events.filter((item) => isAfter(new Date(item.eventDate), startOfDay(new Date()))).length;

    return (
        <div className="d-flex flex-column h-100">
            <div className="d-flex flex-column h-100">
                <div className="h-100 pb-0 overflow-hidden">
                    <FullCalendar
                        locale={i18n.language ?? 'es'}
                        height={'100%'}
                        expandRows={true}
                        selectable={true}
                        plugins={[dayGridPlugin]}
                        initialView="dayGridMonth"
                        fixedWeekCount={false}
                        dayMaxEvents={0}
                        moreLinkContent={function (arg) {
                            let args = <span style={{ position: 'relative', bottom: '16px', color: '#D5D5D5' }}>&bull;</span>;
                            return args;
                        }}
                        headerToolbar={{
                            right: 'next nextYear',
                            center: 'title',
                            left: 'prevYear prev',
                        }}
                        events={events?.map((e) => ({
                            id: e._id,
                            title: e.contract.name,
                            start: new Date(e.eventDate),
                            end: new Date(e.eventDate),
                            allDay: true,
                        }))}
                    // eventResize={}
                    // eventDrop={}
                    />
                </div>
                <div className="pt-2">
                    <span className="text-left" style={{ font: 'normal normal 600 18px/23px Muli' }}>
                        {t('dashboard.label.today')}
                    </span>
                    <div className="clearfix">
                        <span className="float-left text-muted">{t('dashboard.events.event', { count: todayEventcounter })}</span>
                        <Button
                            color="link"
                            className="float-right p-0"
                            onClick={() => {
                                toggle('events');
                            }}
                        >
                            {t('dashboard.label.show')} <span className="text-lowercase">{t('dashboard.label.events')}</span>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

function EventElement({ events, toggle }) {

    const { t } = useTranslation();
    return (
        <div className="smart-wrapper">
            <div className="smart-body-wrap">
                {events?.map((event) => (
                    <Event event={event} key={event._id}></Event>
                ))}
            </div>
            <CardFooter className="bg-transparent border-white text-center mb-2">
                <Button
                    color="link"
                    onClick={() => {
                        toggle('calendar');
                    }}
                    className="float-center p-0"
                >
                    {t('dashboard.label.show')} <span className="text-lowercase">{t('dashboard.label.calendar')}</span>
                </Button>
            </CardFooter>
        </div>
    )
}

function Events() {
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState('calendar');

    const toggle = (tab) => activeTab !== tab && setActiveTab(tab);

    const [dates] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });

    const { data: events = [] } = useQuery(['fetch-events', dates], () => fetcher(fetchEvents(dates)), {
        keepPreviousData: true,
        refetchOnWindowFocus: false
    });

    return (
        <Col xl="3" className="mb-4 px-75">
            <Card className="border bg-white shadow rounded-lg h-100" style={{ minHeight: '450px' }}>
                <CardHeader className="rounded-lg bg-transparent border-white text-left">
                    <Nav>
                        {['calendar', 'events'].map((name) => (
                            <NavItem key={name}>
                                <NavLink
                                    className={
                                        clsx('custom-nav-style px-0 ml-3', {
                                            'active-tab': activeTab === name,
                                        })
                                    }
                                    onClick={() => {
                                        toggle(name);
                                    }}
                                >
                                    {t(`dashboard.label.${name}`)}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>
                </CardHeader>
                <CardBody className="h-100 pt-0">
                    {{
                        'calendar': <CalendarElement events={events} toggle={toggle} />,
                        'events': <EventElement events={events} toggle={toggle} />
                    }[activeTab]}
                </CardBody>
            </Card>
        </Col>
    );
}
export default function Dashboard() {
    return (
        <div className="pt-4 px-4 h-100">
            <div className="h-100 d-flex flex-column">
                <Row>
                    <Overview />
                    <Profile />
                </Row>
                <Row className="flex-grow-1">
                    <Activity />
                    <Stats />
                    <Events />
                </Row>
            </div>
        </div>
    );
}
